/**
 * The maintenance handler the loader hands `initApiClient` (layered shell surfaces, D18, WS-6
 * §3.7). It replaces `MaintenanceProvider`: the notice is a system surface in `#zion-maintenance`.
 */
import type { MaintenanceHandler } from '@zion/sdk-react/auth-core';
import type { MaintenanceNotice } from '@zion/shell-system-surfaces/protocol';

/**
 * Build the handler.
 *
 * - `onMaintenanceStart(retryAfter)` calls `show({ retryAfterSeconds: retryAfter, retrying: false })`
 *   and remembers `retryAfter`.
 * - `onMaintenanceRetryFailed()` calls `show` with the remembered value, or `0` before any start,
 *   and `retrying: true`.
 * - `onMaintenanceEnd()` calls `hide()`.
 *
 * A `retryAfter` that is not a non-negative integer is sent as `0`, the protocol's reading. Holds
 * one number; no other state, no I/O.
 */
export function createMaintenanceBridge(show: (notice: MaintenanceNotice) => void, hide: () => void): MaintenanceHandler {
  let lastRetryAfter = 0;
  return {
    onMaintenanceStart(retryAfter: number) {
      lastRetryAfter = Number.isInteger(retryAfter) && retryAfter >= 0 ? retryAfter : 0;
      show({ retryAfterSeconds: lastRetryAfter, retrying: false });
    },
    onMaintenanceRetryFailed() {
      show({ retryAfterSeconds: lastRetryAfter, retrying: true });
    },
    onMaintenanceEnd() {
      hide();
    },
  };
}
